import type { ViewController } from '@presentation/shell/NexxusShell';
import type { AuditLogEntry } from '@shared/types/AuditLog';
import { listTenantAuditLogs, recordAuditEvent } from '@application/audit/AuditDirectoryService';
import { downloadCsv } from '@presentation/shell/download';
import { esc } from '@presentation/shell/format';
import { ToastService } from '@presentation/components/Toast';

const LEVEL_COLOR: Record<AuditLogEntry['level'], string> = {
  info: '#1B4F72',
  success: '#2E8B57',
  warning: '#8A6000',
  error: '#C0392B',
};

const AFFICHAGE_MAX = 50;

function fmtDateTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso);
  return `${d.toLocaleDateString('fr-FR')} ${d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}`;
}

export const auditController: ViewController = async (section, ctx) => {
  const tenantId = ctx.tenantContext.tryGetTenant()?.id;
  const allEntries = (await listTenantAuditLogs(ctx, tenantId)).sort((a, b) =>
    b.date.localeCompare(a.date),
  );
  // Affichage tronqué pour lisibilité ; l’export (A-10) prend tout.
  const entries = allEntries.slice(0, AFFICHAGE_MAX);

  const timeline = section.querySelector<HTMLElement>('#audit-timeline');
  if (timeline) {
    const hint =
      allEntries.length > AFFICHAGE_MAX
        ? `<p style="font-size:12px;color:var(--gris);margin:0 0 8px;">Affichage des ${AFFICHAGE_MAX} dernières actions sur ${allEntries.length}. L’export CSV contient l’intégralité.</p>`
        : '';
    timeline.innerHTML =
      entries.length === 0
        ? '<div class="empty" style="padding:24px;"><p>Aucune action enregistrée.</p></div>'
        : hint +
          entries
            .map((e) => {
              const color = LEVEL_COLOR[e.level] ?? LEVEL_COLOR.info;
              return `<div style="display:flex;gap:12px;padding:10px 0;border-bottom:1px solid var(--neige);">
            <span style="width:10px;height:10px;border-radius:50%;background:${color};flex:0 0 auto;margin-top:5px;"></span>
            <div style="flex:1;">
              <div style="font-size:13px;"><strong>${esc(e.description)}</strong></div>
              <div style="font-size:11px;color:var(--gris);">${esc(fmtDateTime(e.date))} · ${esc(e.utilisateur)} · ${esc(e.action)}${e.entity ? ' · ' + esc(e.entity) : ''}${e.hash ? ' · ✦' : ''}</div>
            </div>
          </div>`;
            })
            .join('');
  }

  const w = window as unknown as Record<string, unknown>;
  w['exportAudit'] = () => {
    void (async () => {
      if (allEntries.length === 0) {
        ToastService.warning('Aucune entrée à exporter');
        return;
      }
      const header = 'date;utilisateur;action;entite;niveau;description;hash';
      const lines = allEntries.map((e) =>
        [
          e.date,
          e.utilisateur,
          e.action,
          e.entity ?? '',
          e.level,
          (e.description ?? '').replace(/[;\n]/g, ' '),
          e.hash ?? '',
        ].join(';'),
      );
      downloadCsv(
        [header, ...lines].join('\n'),
        `audit_${new Date().toISOString().slice(0, 10)}.csv`,
      );
      await recordAuditEvent(ctx, {
        action: 'export',
        entity: 'audit_log',
        description: `Export journal d’audit (${allEntries.length} lignes)`,
      });
      ToastService.success(`Journal d’audit exporté (${allEntries.length} ligne(s))`);
    })();
  };
};
